import {  ofType, StateObservable } from "redux-observable";
import { ajax } from "rxjs/ajax";
import {catchError, exhaustMap, map, mergeMap } from "rxjs/operators";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import { ProjectLogTag, ProjectLogTagId, ProjectLogTagProp} from "../../models/projects/ProjectLogTag";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {Identifier} from "../../models/common/Identifier";
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import { Observable, of } from 'rxjs';
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import { RouteMethods} from "../../config/RouteConfig";
import {CreateProjectLogEntryActionTypes} from "./ProjectLogActions";
import {Nullable} from "../../models/common/Nullable";
import {ProjectLogModule} from "../../models/projects/ProjectLog";


export type ProjectLogTagActionTypes = FetchProjectLogTagsActionTypes
    | CreateProjectLogEntryActionTypes
    | UpdateProjectLogTagActionTypes
    | DeleteProjectLogTagActionTypes
    | RestoreProjectLogTagActionTypes

/**
 * API PATHS
 */
const uri = "projects/logs/tags"
const basePath = () => `${ APP_CONFIG.baseWeb }${ uri }`;

function pathWithId( id: Identifier<number> ) {
    return basePath() + "/" + id.rawValue;
}

/**
 * return the route / path to fetch tags from api backend for async select components
 *
 * @see AsyncSelect
 *
 * @param projectId when not set, will be set to 0 to fetch all tags
 */
export function projectLogTagSelectRoute( projectId: Nullable< Identifier<number> > = null ) {
    return uri + "/select/" + ( projectId !== null ? projectId!!.rawValue : 0 );
}

/**
 * FETCH TAGS
 */
// typescript redux action types
export interface FetchProjectLogTagsAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_LOG_TAGS> {}
export interface FetchProjectLogTagsSuccessAction extends ReduxAction<typeof FETCH_PROJECT_LOG_TAGS_SUCCESS> {
    payload: ApiReturnType< ProjectLogTag[] >
}
export interface FetchProjectLogTagsErrorAction extends ErrorAction<typeof FETCH_PROJECT_LOG_TAGS_ERROR> {}

export type FetchProjectLogTagsActionTypes = FetchProjectLogTagsAction | FetchProjectLogTagsSuccessAction | FetchProjectLogTagsErrorAction

//redux action type constants
export const FETCH_PROJECT_LOG_TAGS = "FETCH_PROJECT_LOG_TAGS";
export const FETCH_PROJECT_LOG_TAGS_SUCCESS = "FETCH_PROJECT_LOG_TAGS_SUCCESS";
export const FETCH_PROJECT_LOG_TAGS_ERROR = "FETCH_PROJECT_LOG_TAGS_ERROR";

//redux accessor functions
export function fetchProjectLogTags( viewIdentifier: string ): FetchProjectLogTagsAction {
    return { type: FETCH_PROJECT_LOG_TAGS, viewIdentifier };
}
export function fetchProjectLogTagsSuccess( payload: ApiReturnType< ProjectLogTag[] > ): FetchProjectLogTagsSuccessAction {
    return { type: FETCH_PROJECT_LOG_TAGS_SUCCESS, payload };
}
export function fetchProjectLogTagsError( error: any ): FetchProjectLogTagsErrorAction {
    return { type: FETCH_PROJECT_LOG_TAGS_ERROR, error };
}

//redux-observable epic
export function fetchProjectLogTagsEpic(
    action$: Observable< FetchProjectLogTagsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_LOG_TAGS ),
        mergeMap( action => {
            const request = getUserViewSettingsApiRequest( basePath(), action.viewIdentifier, store$ );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => fetchProjectLogTagsSuccess( response.response as ApiReturnType< ProjectLogTag[] > ) ),
                catchError( error => of(
                    fetchProjectLogTagsError( error  ),
                    eventNotificationError( error, store$, action.viewIdentifier, "fetchProjectLogTags" )
                ))
            );
        })
    );
}

/**
 * CREATE TAG
 */
export interface CreateProjectLogTagAction extends ProjectLogTagProp, ReduxAction<typeof CREATE_PROJECT_LOG_TAG> {}
export interface CreateProjectLogTagSuccessAction extends ReduxAction<typeof CREATE_PROJECT_LOG_TAG_SUCCESS> {}
export interface CreateProjectLogTagErrorAction extends ErrorAction<typeof CREATE_PROJECT_LOG_TAG_ERROR> {}

export type CreateProjectLogActionTypes = CreateProjectLogTagErrorAction | CreateProjectLogTagSuccessAction | CreateProjectLogTagAction

export const CREATE_PROJECT_LOG_TAG = "CREATE_PROJECT_LOG_TAG";
export const CREATE_PROJECT_LOG_TAG_SUCCESS = "CREATE_PROJECT_LOG_TAG_SUCCESS";
export const CREATE_PROJECT_LOG_TAG_ERROR = "CREATE_PROJECT_LOG_TAG_ERROR";


export function createProjectLogTag( projectLogTag: ProjectLogTag ): CreateProjectLogTagAction {
    return { type: CREATE_PROJECT_LOG_TAG, projectLogTag };
}
export function createProjectLogTagSuccess(): CreateProjectLogTagSuccessAction {
    return { type: CREATE_PROJECT_LOG_TAG_SUCCESS };
}
export function createProjectLogTagError( error: any): CreateProjectLogTagErrorAction {
    return { type: CREATE_PROJECT_LOG_TAG_ERROR, error };
}

export function createProjectLogTagEpic(
    action$: Observable< CreateProjectLogTagAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_LOG_TAG ),
        exhaustMap( action => {
            return ajax( APP_CONFIG.getRequestObject( basePath(), RouteMethods.post, action.projectLogTag ) ).pipe(
                mergeMap( _ => of(
                    createProjectLogTagSuccess(),
                    eventNotificationSuccess(),
                    fetchProjectLogTags( ProjectLogModule.identifier )
                ) ),
                catchError( error => of(
                    createProjectLogTagError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "createProjectLogTag" )
                ))
            );
        })
    );
}

/**
 * UPDATE TAG
 */
//typescript redux action types
export interface UpdateProjectLogTagAction extends ProjectLogTagProp, ReduxAction<typeof UPDATE_PROJECT_LOG_TAG> {}
export interface UpdateProjectLogTagSuccessAction extends ReduxAction<typeof UPDATE_PROJECT_LOG_TAG_SUCCESS> {}
export interface UpdateProjectLogTagErrorAction extends ErrorAction<typeof UPDATE_PROJECT_LOG_TAG_ERROR> {}

export type UpdateProjectLogTagActionTypes = UpdateProjectLogTagAction | UpdateProjectLogTagSuccessAction | UpdateProjectLogTagErrorAction

//redux action type constants
export const UPDATE_PROJECT_LOG_TAG = "UPDATE_PROJECT_LOG_TAG";
export const UPDATE_PROJECT_LOG_TAG_SUCCESS = "UPDATE_PROJECT_LOG_TAG_SUCCESS";
export const UPDATE_PROJECT_LOG_TAG_ERROR = "UPDATE_PROJECT_LOG_TAG_ERROR";

//redux action accessor functions
export function updateProjectLogTag( projectLogTag: ProjectLogTag ): UpdateProjectLogTagAction {
    return { type: UPDATE_PROJECT_LOG_TAG, projectLogTag };
}
export function updateProjectLogTagSuccess(): UpdateProjectLogTagSuccessAction {
    return { type: UPDATE_PROJECT_LOG_TAG_SUCCESS };
}
export function updateProjectLogTagError( error: any ): UpdateProjectLogTagErrorAction {
    return { type: UPDATE_PROJECT_LOG_TAG_ERROR, error };
}

//redux observable epic
export function updateProjectLogTagEpic(
    action$: Observable< UpdateProjectLogTagAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_LOG_TAG ),
        exhaustMap( action => {
            return ajax(
                APP_CONFIG.getRequestObject(
                    pathWithId( action.projectLogTag.projectLogTagId ),
                    RouteMethods.put,
                    action.projectLogTag
                )
             ).pipe(
                mergeMap( _ => of(
                    updateProjectLogTagSuccess(),
                    eventNotificationSuccess(),
                    fetchProjectLogTags( ProjectLogModule.identifier )
                )),
                catchError( error => of(
                    updateProjectLogTagError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "updateProjectLogTag" )
                ) )
            )
        })
    );
}

/**
 * DELETE TAG
 */
//typescript redux action types
export interface DeleteProjectLogTagAction extends ProjectLogTagId, ReduxAction<typeof DELETE_PROJECT_LOG_TAG> {}
export interface DeleteProjectLogTagSuccessAction extends ReduxAction<typeof DELETE_PROJECT_LOG_TAG_SUCCESS> {}
export interface DeleteProjectLogTagErrorAction extends ErrorAction<typeof DELETE_PROJECT_LOG_TAG_ERROR> {}

export type DeleteProjectLogTagActionTypes = DeleteProjectLogTagAction | DeleteProjectLogTagSuccessAction | DeleteProjectLogTagErrorAction

//redux action type constants
export const DELETE_PROJECT_LOG_TAG = "DELETE_PROJECT_LOG_TAG";
export const DELETE_PROJECT_LOG_TAG_SUCCESS = "DELETE_PROJECT_LOG_TAG_SUCCESS";
export const DELETE_PROJECT_LOG_TAG_ERROR = "DELETE_PROJECT_LOG_TAG_ERROR";

//redux action accessor functions
export function deleteProjectLogTag( projectLogTagId: Identifier<number> ): DeleteProjectLogTagAction {
    return { type: DELETE_PROJECT_LOG_TAG, projectLogTagId };
}
export function deleteProjectLogTagSuccess(): DeleteProjectLogTagSuccessAction {
    return { type: DELETE_PROJECT_LOG_TAG_SUCCESS };
}
export function deleteProjectLogTagError( error: any ): DeleteProjectLogTagErrorAction {
    return { type: DELETE_PROJECT_LOG_TAG_ERROR, error };
}

//redux observable epic
export function deleteProjectLogTagEpic(
    action$: Observable< DeleteProjectLogTagAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_LOG_TAG ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject( pathWithId( action.projectLogTagId ), RouteMethods.delete )
        ).pipe(
            mergeMap( _ => of(
                deleteProjectLogTagSuccess(),
                eventNotificationSuccess(),
                fetchProjectLogTags( ProjectLogModule.identifier )
            ) ),
            catchError( error => of(
                deleteProjectLogTagError( error ),
                eventNotificationError( error, store$, ProjectLogModule.identifier, "deleteProjectLogTag" )
            ))
        ))
    );
}

/**
 * RESTORE TAG
 */
//typescript redux action types
export interface RestoreProjectLogTagAction extends ProjectLogTagId, ReduxAction< typeof RESTORE_PROJECT_LOG_TAG> {}
export interface RestoreProjectLogTagSuccessAction extends ReduxAction< typeof RESTORE_PROJECT_LOG_TAG_SUCCESS> {}
export interface RestoreProjectLogTagErrorAction extends ErrorAction< typeof RESTORE_PROJECT_LOG_TAG_ERROR> {}

export type RestoreProjectLogTagActionTypes = RestoreProjectLogTagAction | RestoreProjectLogTagSuccessAction | RestoreProjectLogTagErrorAction

//redux action type constants
export const RESTORE_PROJECT_LOG_TAG = "RESTORE_PROJECT_LOG_TAG";
export const RESTORE_PROJECT_LOG_TAG_SUCCESS = "RESTORE_PROJECT_LOG_TAG_SUCCESS";
export const RESTORE_PROJECT_LOG_TAG_ERROR = "RESTORE_PROJECT_LOG_TAG_ERROR";

//redux action accessor functions
export function restoreProjectLogTag( projectLogTagId: Identifier<number> ): RestoreProjectLogTagAction {
    return { type: RESTORE_PROJECT_LOG_TAG, projectLogTagId };
}
export function restoreProjectLogTagSuccess(): RestoreProjectLogTagSuccessAction {
    return { type: RESTORE_PROJECT_LOG_TAG_SUCCESS };
}
export function restoreProjectLogTagError( error: any ): RestoreProjectLogTagErrorAction {
    return { type: RESTORE_PROJECT_LOG_TAG_ERROR, error };
}

//redux observable epic
export function restoreProjectLogTagEpic(
    action$: Observable< RestoreProjectLogTagAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_PROJECT_LOG_TAG ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject( pathWithId( action.projectLogTagId ), RouteMethods.patch )
        ).pipe(
            mergeMap( _ => of(
                restoreProjectLogTagSuccess(),
                eventNotificationSuccess(),
                fetchProjectLogTags( ProjectLogModule.identifier )
            ) ),
            catchError( error => of(
                restoreProjectLogTagError( error ),
                eventNotificationError( error, store$, ProjectLogModule.identifier, "restoreProjectLogTag" )
            ) )
        ))
    );
}